import { apiCall, API_CONFIG } from './api';
import { formatDateOnly } from '../utils/utils';

export const dispatchAPI = {
  // Fetch confirmed orders with date and timeSlot filters
  getConfirmedOrders: async (date = null, timeSlot = null) => {
    try {
      const params = new URLSearchParams();

      if (date) params.append('date', formatDateOnly(date));

      if (timeSlot && timeSlot !== 'ALL ORDERS') {
        params.append('timeSlot', timeSlot);
      }

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ORDER_LIST_CONFIRMED}?${params.toString()}`,
        { method: 'GET' }
      );

      return response;
    } catch (error) {
      console.error('Failed to fetch confirmed orders:', error);
      throw error;
    }
  },

  // Fetch dispatch details (time slots + drivers)
  getDispatchDetails: async (date = null) => {
    try {
      let url = API_CONFIG.ENDPOINTS.DISPATCH_FETCH;
      if (date) {
        url += `?date=${formatDateOnly(date)}`;
      }
      const response = await apiCall(url, { method: 'GET' });
      return response;
    } catch (error) {
      console.error('Failed to fetch dispatch details:', error);
      throw error;
    }
  },

  // Plan dispatch routes
  planRoutes: async (payload) => {
    try {
      // Ensure date is formatted consistently if present in payload
      if (payload && payload.date) {
        payload.date = formatDateOnly(payload.date);
      }
      
      const response = await apiCall(API_CONFIG.ENDPOINTS.DISPATCH_PLAN_ROUTES, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      return response;
    } catch (error) {
      console.error('Failed to plan routes:', error);
      return {
        success: false,
        msg: error.message || 'Failed to plan routes',
        data: null
      };
    }
  },

  checkUsedOrdersAndRoute: async (currentSlot, currentDate) => {
    try {
      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ROUTE_HISTORY}?slot=${encodeURIComponent(currentSlot)}&date=${formatDateOnly(currentDate)}`,
        { method: 'GET' }
      );

      const routes = response.success ? (response.data || []) : (Array.isArray(response) ? response : []);
      const usedOrderSet = new Set();
      let existingRoute = null;

      if (routes && Array.isArray(routes)) {
        if (routes.length > 0) {
          existingRoute = routes[0];
        }

        routes.forEach(route => {
          if (route.plans && Array.isArray(route.plans)) {
            route.plans.forEach(plan => {
              if (plan.stops && Array.isArray(plan.stops)) {
                plan.stops.forEach(stop => {
                  if (stop.order) {
                    usedOrderSet.add(stop.order);
                  }
                });
              }
            });
          }
        });
      }

      return { usedOrderSet, existingRoute };
    } catch (error) {
      console.error('Error checking used orders and route:', error);
      return { usedOrderSet: new Set(), existingRoute: null };
    }
  },

  saveRouteToHistory: async (plans, timeSlot, date, slotId = null) => {
    try {
      const requestBody = {
        slot: timeSlot,
        date: formatDateOnly(date),
        plans: plans,
        ...(slotId ? { slotId } : {})
      };

      const response = await apiCall(API_CONFIG.ENDPOINTS.ROUTE_HISTORY, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      return response;
    } catch (error) {
      console.error('Error saving route to history:', error);
      throw error;
    }
  },

  updateRouteInHistory: async (routeId, plans, timeSlot, date, slotId = null) => {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.ROUTE_HISTORY}/${routeId}`;
      const response = await apiCall(endpoint, {
        method: 'PUT',
        body: JSON.stringify({
          plans: plans,
          slot: timeSlot,
          date: formatDateOnly(date),
          ...(slotId ? { slotId } : {})
        }),
      });

      return response;
    } catch (error) {
      console.error('Error updating route in history:', error);
      throw error;
    }
  },

  deleteRouteHistory: async (routeId) => {
    try {
      const endpoint = `${API_CONFIG.ENDPOINTS.DISPATCH_HISTORY}/${routeId}`;
      const response = await apiCall(endpoint, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting route from history:', error);
      throw error;
    }
  },

  // Fetch route overview for a specific driver
  getRouteOverview: async (driver, date, slot, routeId = null) => {
    try {
      const params = new URLSearchParams();
      if (driver) params.append('driver', driver);
      if (date) params.append('date', formatDateOnly(date));
      if (slot) params.append('slot', slot);
      if (routeId) params.append('routeId', routeId);

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.ROUTE_OVERVIEW}?${params.toString()}`,
        { method: 'GET' }
      );

      return response;
    } catch (error) {
      console.error('Failed to fetch route overview:', error);
      return {
        success: false,
        msg: error.message || 'Failed to fetch route overview',
        data: null
      };
    }
  },

  getDispatchHistory: async (page = 1, limit = 20, filterType = 'today', startDate = null, slot = null, id = null) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('filterType', filterType);
      if (startDate) params.append('startDate', formatDateOnly(startDate));
      if (slot && slot !== 'ALL') params.append('slot', slot);
      if (id) params.append('id', id);

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.DISPATCH_HISTORY}?${params.toString()}`,
        { method: 'GET' }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch dispatch history:', error);
      return {
        success: false,
        msg: error.message || 'Failed to fetch dispatch history',
        data: { routes: [], pagination: {} }
      };
    }
  },

  getDispatchHistorySlots: async (filterType = 'today', startDate = null) => {
    try {
      const params = new URLSearchParams();
      params.append('filterType', filterType);
      if (startDate) params.append('startDate', formatDateOnly(startDate));

      const response = await apiCall(
        `${API_CONFIG.ENDPOINTS.DISPATCH_HISTORY_SLOTS}?${params.toString()}`,
        { method: 'GET' }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch dispatch history slots:', error);
      return {
        success: false,
        data: []
      };
    }
  },
};